import {
    Box,
    CircularProgress,
    Tab,
    Tabs,
    TableFooter,
    TablePagination,
    TableRow,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import React, { useEffect, useMemo, useRef, useState } from "react";

import LoadingOverlay from "./components/LoadingOverlay";
import { fetchDealSummaryStream } from "./dataHandler";

// Available prompt types
const PROMPT_TYPES = [
    { id: "sales", name: "Sales" },
    { id: "implementation", name: "Implementation" },
];

// Local storage key for saving last selected prompt type
const PROMPT_TYPE_STORAGE_KEY = "dealSummary_promptType";

// Get saved prompt type from localStorage or default to "sales"
const getSavedPromptType = () => {
    const saved = localStorage.getItem(PROMPT_TYPE_STORAGE_KEY);
    return saved && PROMPT_TYPES.some((p) => p.id === saved) ? saved : "sales";
};

// Save prompt type to localStorage
const savePromptType = (promptType) => {
    localStorage.setItem(PROMPT_TYPE_STORAGE_KEY, promptType);
};

// In-memory cache for deal list (used for deal details display)
let dealListCache = null;
// Track in-flight requests to prevent duplicates (keyed by `${dealId}_${promptType}`)
const fetchingDeals = {};
// Preserve scroll position when toggling views
let savedScrollPosition = 0;

const DealSummaryTab = ({ fetchData, resetKey, customerList }) => {
    const [dealData, setDealData] = useState([]);
    const [dealSummary, setDealSummary] = useState(null);
    const [dealResponse, setDealResponse] = useState(null);
    const [listLoading, setListLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedPromptType, setSelectedPromptType] = useState(getSavedPromptType());

    // Create customer map keyed by NetSuite ID
    const customerMap = useMemo(() => {
        if (!customerList) return {};
        const map = {};
        customerList.forEach((c) => {
            if (c.id) map[String(c.id)] = c;
        });
        return map;
    }, [customerList]);

    // Ref to store abort function for current stream (to cancel when switching tabs)
    const abortStreamRef = useRef(null);

    // Check for dealid URL parameter and use state for visibility
    const urlParams = new URLSearchParams(window.location.search);
    const initialDealId = urlParams.get("dealid");
    const [selectedDealId, setSelectedDealId] = useState(initialDealId);

    // Reset deal view when resetKey changes (triggered by sidebar navigation)
    useEffect(() => {
        if (resetKey > 0) {
            setSelectedDealId(null);
            setDealSummary(null);
            setDealResponse(null);
        }
    }, [resetKey]);

    // Fetch deal summary for a specific prompt type (with streaming)
    const fetchSummaryForPromptType = (dealId, promptType) => {
        const fetchKey = `${dealId}_${promptType}`;

        // Abort any existing stream before starting a new one
        if (abortStreamRef.current) {
            // Clear the fetchingDeals flag for the aborted stream
            Object.keys(fetchingDeals).forEach((key) => delete fetchingDeals[key]);
            abortStreamRef.current();
            abortStreamRef.current = null;
        }

        // Skip if already fetching this deal/prompt combination
        if (fetchingDeals[fetchKey]) {
            return;
        }

        // Fetch deal summary with streaming
        fetchingDeals[fetchKey] = true;
        setDetailLoading(true);
        setDealSummary("");

        let accumulatedSummary = "";
        let isCached = false;

        const abortFn = fetchDealSummaryStream(dealId, promptType, {
            onDealData: (dealData) => {
                setDealResponse({ dealData });
            },
            onSection: (content) => {
                accumulatedSummary += content;
                setDealSummary(accumulatedSummary);
            },
            onCached: (data) => {
                // Server returned cached response
                console.log("[dealSummary] Cached data received:", data);
                isCached = true;
                setDealResponse(data);
                setDealSummary(data.summary || "");
                setDetailLoading(false);
                delete fetchingDeals[fetchKey];
                abortStreamRef.current = null;
            },
            onDone: () => {
                // Skip if cached response already handled
                if (isCached) return;

                // Replace with complete content to clean up any partial rendering
                setDealSummary(accumulatedSummary);

                setDetailLoading(false);
                delete fetchingDeals[fetchKey];
                abortStreamRef.current = null;
            },
            onError: (err) => {
                console.error("Error fetching deal:", err);
                setDetailLoading(false);
                delete fetchingDeals[fetchKey];
                abortStreamRef.current = null;
            },
        });

        // Store abort function to cancel stream if user switches tabs
        abortStreamRef.current = abortFn;
    };

    // Navigate to deal detail (just toggles visibility, fetches summary)
    const navigateToDeal = (dealId) => {
        // Save scroll position before navigating
        savedScrollPosition = window.scrollY;

        const url = new URL(window.location);
        url.searchParams.set("dealid", dealId);
        window.history.pushState({}, "", url);
        setSelectedDealId(dealId);
        setDealSummary(null); // Clear old summary to prevent showing stale data

        // Use saved prompt type from localStorage
        const savedType = getSavedPromptType();
        setSelectedPromptType(savedType);

        // Scroll to top for detail view
        window.scrollTo(0, 0);

        // Note: AI summary fetch is triggered by useEffect when selectedDeal is available
    };

    // Handle prompt type tab change
    const handlePromptTypeChange = (event, newValue) => {
        const promptType = PROMPT_TYPES[newValue].id;
        setSelectedPromptType(promptType);
        savePromptType(promptType);
        setDealSummary(""); // Clear current summary immediately
        if (selectedDealId) {
            fetchSummaryForPromptType(selectedDealId, promptType);
        }
    };

    // Navigate back to list (just toggles visibility)
    const navigateToList = () => {
        const url = new URL(window.location);
        url.searchParams.delete("dealid");
        window.history.pushState({}, "", url);
        setSelectedDealId(null);

        // Restore scroll position after state update
        setTimeout(() => window.scrollTo(0, savedScrollPosition), 0);
    };

    // Handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const dealId = params.get("dealid");
            setSelectedDealId(dealId);
            if (dealId && !dealSummary) {
                // Fetch deal summary if navigating forward to a deal
                navigateToDeal(dealId);
            }
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [dealSummary]);

    // Fetch deal list on mount (once)
    useEffect(() => {
        const loadList = async () => {
            try {
                setListLoading(true);
                const response = await fetchData("getdeallist");
                if (response?.values) {
                    const results = response.values.results || [];
                    setDealData(results);
                    dealListCache = results;
                }
            } catch (err) {
                console.error("Error fetching deal list:", err);
                setError(err.message);
            } finally {
                setListLoading(false);
            }
        };

        loadList();

        // Note: AI summary fetch is handled by separate useEffect when selectedDeal is available
        // No need to call navigateToDeal here - selectedDealId is already set from URL
    }, [fetchData]);

    const columns = [
        {
            name: "id",
            label: "Deal ID",
            options: {
                display: false,
                filter: false,
            },
        },
        {
            name: "dealname",
            label: "Deal Name",
            options: {
                filter: false,
                customBodyRender: (value, tableMeta) => {
                    const dealName = value || "-";
                    const id = tableMeta.rowData[0]; // ID is first column
                    if (id) {
                        const url = new URL(window.location);
                        url.searchParams.set("dealid", id);
                        return (
                            <a
                                href={url.toString()}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigateToDeal(id);
                                }}
                                style={{
                                    color: "#2bbcc0",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                }}
                            >
                                {dealName}
                            </a>
                        );
                    }
                    return dealName;
                },
                setCellProps: () => ({ style: { minWidth: "200px" } }),
            },
        },
        {
            name: "companyId",
            label: "Company ID",
            options: {
                display: false,
                filter: false,
                customBodyRender: (value) => value || "-",
            },
        },
        {
            name: "companyName",
            label: "Company",
            options: {
                display: false,
                filter: false,
                customBodyRender: (value) => value || "-",
            },
        },
        {
            name: "netsuiteId",
            label: "NetSuite ID",
            options: {
                display: false,
                filter: false,
                customBodyRender: (value) => value || "-",
            },
        },
        {
            name: "pipelineName",
            label: "Pipeline",
            options: {
                customBodyRender: (value) => value || "-",
            },
        },
        {
            name: "stageName",
            label: "Stage",
            options: {
                customBodyRender: (value) => value || "-",
            },
        },
        {
            name: "ownerName",
            label: "Owner",
            options: {
                customBodyRender: (value) => value || "-",
            },
        },
        {
            name: "amount",
            label: "Amount",
            options: {
                filter: false,
                customBodyRender: (value) => {
                    if (value) {
                        return `$${parseFloat(value).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                        })}`;
                    }
                    return "-";
                },
                setCellProps: () => ({ style: { textAlign: "right" } }),
            },
        },
        {
            name: "closedate",
            label: "Close Date",
            options: {
                filter: false,
                customBodyRender: (value) => {
                    if (value) {
                        return new Date(value).toLocaleDateString("en-US");
                    }
                    return "-";
                },
            },
        },
    ];

    const options = {
        selectableRows: "none",
        searchAlwaysOpen: true,
        storageKey: "dealSummaryTable",
        filter: true,
        sort: true,
        sortOrder: { name: "dealname", direction: "asc" },
        customSort: (data, colIndex, order) => {
            const columnNames = [
                "id",
                "dealname",
                "companyId",
                "companyName",
                "netsuiteId",
                "pipelineName",
                "stageName",
                "ownerName",
                "amount",
                "closedate",
            ];
            const colName = columnNames[colIndex];

            return data.sort((a, b) => {
                let valA, valB;

                if (colName === "amount") {
                    valA = parseFloat(a.data[colIndex]) || 0;
                    valB = parseFloat(b.data[colIndex]) || 0;
                    return (valA - valB) * (order === "asc" ? 1 : -1);
                } else if (colName === "closedate") {
                    valA = new Date(a.data[colIndex] || 0).getTime();
                    valB = new Date(b.data[colIndex] || 0).getTime();
                    return (valA - valB) * (order === "asc" ? 1 : -1);
                } else {
                    valA = (a.data[colIndex] || "").toString().toLowerCase();
                    valB = (b.data[colIndex] || "").toString().toLowerCase();
                }

                return valA.localeCompare(valB) * (order === "asc" ? 1 : -1);
            });
        },
        viewColumns: true,
        print: false,
        download: true,
        search: true,
        fixedHeader: true,
        pagination: true,
        rowsPerPage: 250,
        rowsPerPageOptions: [50, 100, 250, 500],
        customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage) => {
            const from = page * rowsPerPage + 1;
            const to = Math.min((page + 1) * rowsPerPage, count);
            return (
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            count={count}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(_, p) => changePage(p)}
                            onRowsPerPageChange={(e) => changeRowsPerPage(e.target.value)}
                            rowsPerPageOptions={[50, 100, 250, 500]}
                            labelDisplayedRows={() =>
                                `${from.toLocaleString()}-${to.toLocaleString()} of ${count.toLocaleString()}`
                            }
                        />
                    </TableRow>
                </TableFooter>
            );
        },
    };

    // Find selected deal from dealData state (falls back to cache)
    const selectedDeal = selectedDealId
        ? dealData?.find((d) => d.id === selectedDealId) ||
          dealListCache?.find((d) => d.id === selectedDealId)
        : null;

    // If deal ID is set but deal not found, ensure deal list is loaded
    useEffect(() => {
        if (selectedDealId && !selectedDeal && !listLoading && dealData.length === 0) {
            const loadList = async () => {
                try {
                    setListLoading(true);
                    const response = await fetchData("getdeallist");
                    if (response?.values) {
                        const results = response.values.results || [];
                        setDealData(results);
                        dealListCache = results;
                    }
                } catch (err) {
                    console.error("Error fetching deal list:", err);
                } finally {
                    setListLoading(false);
                }
            };
            loadList();
        }
    }, [selectedDealId, selectedDeal, listLoading, dealData.length, fetchData]);

    // Fetch AI summary only after deal details are available
    useEffect(() => {
        // Wait for deal list to be loaded (dealData will never be empty in production)
        if (dealData.length === 0) {
            // console.log("[dealSummary] Waiting for deal list to load...");
            return;
        }
        // Only fetch if deal is selected, deal details are loaded, and no summary yet
        if (selectedDealId && selectedDeal && dealSummary === null && !detailLoading) {
            // console.log("[dealSummary] Triggering fetch for deal:", selectedDealId);
            fetchSummaryForPromptType(selectedDealId, selectedPromptType);
        }
    }, [
        selectedDealId,
        selectedDeal,
        dealSummary,
        detailLoading,
        selectedPromptType,
        dealData.length,
    ]);

    return (
        <div className="flex w-full flex-col" style={{ margin: "0px", padding: "0px" }}>
            {/* Deal Detail View - shown when dealid is present */}
            <div style={{ display: selectedDealId ? "block" : "none" }}>
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigateToList();
                    }}
                    style={{
                        color: "#2bbcc0",
                        textDecoration: "none",
                        padding: "12px",
                        cursor: "pointer",
                        display: "inline-block",
                    }}
                >
                    ← Back to Deal List
                </a>
                {selectedDeal && (
                    <Box className="p-4" sx={{ borderBottom: "1px solid #e0e0e0" }}>
                        <h2 style={{ fontSize: "1.3rem", fontWeight: 600 }}>
                            {selectedDeal.dealname || "Deal Details"}
                        </h2>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: 2,
                                pl: 2,
                                mt: 2,
                            }}
                        >
                            <div>
                                <strong>Deal ID:</strong>{" "}
                                {selectedDealId ? (
                                    <a
                                        href={`https://app.hubspot.com/contacts/9493287/record/0-3/${selectedDealId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#2bbcc0", textDecoration: "none" }}
                                    >
                                        {selectedDealId} ↗
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </div>
                            <div>
                                <strong>Company:</strong>{" "}
                                {(() => {
                                    if (!selectedDeal.companyName) return "-";
                                    // If customerMap is empty, show loading
                                    if (Object.keys(customerMap).length === 0) return "Loading...";
                                    const names = selectedDeal.companyName.split(", ");
                                    const netsuiteIds = selectedDeal.netsuiteId?.split(", ") || [];
                                    // Filter to only companies that exist in customerMap
                                    const validCompanies = names
                                        .map((name, idx) => ({ name, nsId: netsuiteIds[idx] }))
                                        .filter(({ nsId }) => nsId && customerMap[nsId]);
                                    if (validCompanies.length === 0) return "-";
                                    return validCompanies.map(({ name, nsId }, idx) => (
                                        <span key={idx}>
                                            {idx > 0 && ", "}
                                            <a
                                                href={`/?tab=customers&id=${nsId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: "#2bbcc0",
                                                    textDecoration: "none",
                                                }}
                                            >
                                                {name} ↗
                                            </a>
                                        </span>
                                    ));
                                })()}
                            </div>
                            <div>
                                <strong>Pipeline:</strong> {selectedDeal.pipelineName || "-"}
                            </div>
                            <div>
                                <strong>Stage:</strong> {selectedDeal.stageName || "-"}
                            </div>
                            <div>
                                <strong>Owner:</strong> {selectedDeal.ownerName || "-"}
                            </div>
                            <div>
                                <strong>Amount:</strong>{" "}
                                {selectedDeal.amount
                                    ? `$${parseFloat(selectedDeal.amount).toLocaleString()}`
                                    : "-"}
                            </div>
                            <div>
                                <strong>Close Date:</strong>{" "}
                                {selectedDeal.closedate
                                    ? new Date(selectedDeal.closedate).toLocaleDateString()
                                    : "-"}
                            </div>
                        </Box>
                    </Box>
                )}
                {selectedDeal && (
                    <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
                        <Tabs
                            value={PROMPT_TYPES.findIndex((p) => p.id === selectedPromptType)}
                            onChange={handlePromptTypeChange}
                            aria-label="Summary type tabs"
                        >
                            {PROMPT_TYPES.map((type) => (
                                <Tab key={type.id} label={type.name} />
                            ))}
                        </Tabs>
                    </Box>
                )}
                {detailLoading && !dealSummary && (
                    <Box
                        sx={{
                            p: 4,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 2,
                            color: "text.secondary",
                        }}
                    >
                        <CircularProgress size={20} />
                        Loading{" "}
                        {PROMPT_TYPES.find((p) => p.id === selectedPromptType)?.name ||
                            "Sales"}{" "}
                        summary...
                    </Box>
                )}
                {dealSummary ? (
                    <Box
                        className="deal-summary-content p-4"
                        sx={{
                            "& h3": {
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                color: "text.primary",
                                mt: 2,
                                mb: 1,
                            },
                            "& ul": {
                                pl: 4,
                                listStyleType: "disc",
                                "& li": {
                                    fontSize: "0.95rem",
                                    color: "text.secondary",
                                    mb: 0.5,
                                    display: "list-item",
                                    "&::marker": {
                                        color: "primary.main",
                                    },
                                },
                            },
                        }}
                        dangerouslySetInnerHTML={{ __html: dealSummary }}
                    />
                ) : !detailLoading && dealData.length > 0 ? (
                    <h3 className="px-2 pt-8 text-center">No summary available</h3>
                ) : !detailLoading && dealData.length === 0 ? (
                    <Box
                        sx={{
                            p: 4,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 2,
                            color: "text.secondary",
                        }}
                    >
                        <CircularProgress size={20} />
                        Loading deal data...
                    </Box>
                ) : null}
                {detailLoading && dealSummary && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            py: 3,
                            gap: 2,
                        }}
                    >
                        <CircularProgress size={24} />
                        <span style={{ color: "#666" }}>Loading more sections...</span>
                    </Box>
                )}
                {/* TODO: Remove before production
                {!detailLoading && (
                    <div
                        style={{
                            marginTop: "20px",
                            padding: "12px",
                            background: "#ffffcc",
                            border: "2px solid red",
                            fontSize: "12px",
                            fontFamily: "monospace",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        <strong style={{ color: "red" }}>
                            DEBUG (temporary - will be removed in prod):
                        </strong>
                        <br />
                        {JSON.stringify(dealResponse?.dealData, null, 2)}
                    </div>
                )}
                */}
            </div>

            {/* Deal List View - hidden when dealid is present */}
            <div style={{ display: selectedDealId ? "none" : "block" }}>
                {listLoading && <LoadingOverlay text="Loading deals..." />}
                {error && <h3 className="px-2 pt-8 text-center text-red-500">Error: {error}</h3>}
                {!listLoading && !error && dealData.length > 0 && (
                    <>
                        <p className="mb-2 mt-4 px-3 text-sm text-gray-500">
                            Showing open deals and deals closed in the last 45 days.
                        </p>
                        <MUIDataTable
                            data={dealData}
                            columns={columns}
                            options={options}
                            className="ngShadow"
                        />
                    </>
                )}
                {!listLoading && !error && dealData.length === 0 && (
                    <h3 className="px-2 pt-8 text-center">No deals found</h3>
                )}
            </div>
        </div>
    );
};

export default DealSummaryTab;
